/**
 * 剪线手势的几何计算 —— 纯函数，零 DOM 依赖。
 *
 * 交互是「按住 Ctrl + 右键划过若干条连线，划到哪条删哪条」（Blender 的 Ctrl+RMB）。
 * 判定就是：**这条线的采样点，有没有落在划出的折线附近**。
 *
 * 抽成纯函数的原因很直接：几何边界（退化线段、单点、阈值）最容易写错，
 * 而错了的表现是「划过去没反应」或者「划一条删三条」，
 * 都很难靠肉眼调试。
 */

/*
 * ⚠️ `Point` 原来**在这里和 `frame.ts` 各定义了一遍**（结构一样，
 * 所以 TS 一直没报错）。已下沉到 `types/geometry.ts` —— 它是两者的公共地基，
 * 不属于「裁剪几何」。这里继续转出去，既有引用不动。
 */
import type { Point } from '../types/geometry'
export type { Point } from '../types/geometry'

/** 点到**线段**（不是直线）的距离平方 */
export function distanceToSegmentSq(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy

  // 退化成一个点
  if (lenSq === 0) return (p.x - a.x) ** 2 + (p.y - a.y) ** 2

  // 把 p 投影到线段所在直线上，t 夹在 [0,1] 保证落在线段内
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = t < 0 ? 0 : t > 1 ? 1 : t

  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return (p.x - cx) ** 2 + (p.y - cy) ** 2
}

/**
 * 折线是否「碰到」某个点集。
 *
 * 单点折线（只按下没划动）也支持 —— 退化成「点到点」的距离判定。
 */
export function polylineHitsPoints(
  polyline: readonly Point[],
  points: readonly Point[],
  tolerance: number,
): boolean {
  if (polyline.length === 0 || points.length === 0) return false

  const tolSq = tolerance * tolerance

  if (polyline.length === 1) {
    const only = polyline[0] as Point
    return points.some(
      (p) => (p.x - only.x) ** 2 + (p.y - only.y) ** 2 <= tolSq,
    )
  }

  for (let i = 0; i < polyline.length - 1; i += 1) {
    const a = polyline[i] as Point
    const b = polyline[i + 1] as Point
    for (const p of points) {
      if (distanceToSegmentSq(p, a, b) <= tolSq) return true
    }
  }
  return false
}

/* ══════════════════════════════════════════════════════════════
   线段-线段
   ══════════════════════════════════════════════════════════════ */

/** 叉积符号：判断 c 在 a→b 的哪一侧 */
function orientation(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
}

/** 两条线段是否真正相交（端点接触 / 共线交给距离判定兜底） */
export function segmentsIntersect(
  a1: Point,
  a2: Point,
  b1: Point,
  b2: Point,
): boolean {
  const d1 = orientation(b1, b2, a1)
  const d2 = orientation(b1, b2, a2)
  const d3 = orientation(a1, a2, b1)
  const d4 = orientation(a1, a2, b2)

  const straddleA = (d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)
  const straddleB = (d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0)
  return straddleA && straddleB
}

/**
 * 两条线段的最短距离平方。
 *
 * 相交则为 0；否则「一方端点到另一方线段」的四个距离取最小 —— 这个组合是完备的。
 */
export function segmentDistanceSq(
  a1: Point,
  a2: Point,
  b1: Point,
  b2: Point,
): number {
  if (segmentsIntersect(a1, a2, b1, b2)) return 0
  return Math.min(
    distanceToSegmentSq(a1, b1, b2),
    distanceToSegmentSq(a2, b1, b2),
    distanceToSegmentSq(b1, a1, a2),
    distanceToSegmentSq(b2, a1, a2),
  )
}

/**
 * 两条折线是否「碰到」。
 *
 * ⚠️ 这里是**线段对线段**，不是点对线段。
 *
 * 一开始写成了「用划痕的线段去测对方的**采样点**」，于是命中率取决于采样密度：
 * 边按 ~20px 间距采样、容差只有 6px，划痕只要落在两个采样点中间就会判不中 ——
 * 表现就是「剪线偶尔失败」，而且**边越长越容易漏**。
 *
 * 线段对线段是精确的，跟采样密度解耦：采样只需要够密到贴近曲线本身。
 */
export function polylineHitsPolyline(
  a: readonly Point[],
  b: readonly Point[],
  tolerance: number,
): boolean {
  if (a.length === 0 || b.length === 0) return false
  const tolSq = tolerance * tolerance

  /*
   * 退化成一个点：交给点集版本。
   * ⚠️ polylineHitsPoints 的**第一个参数是提供线段**的一方 ——
   * 单点那一侧要放到第二个参数去，传反了会把「点在线段上」判成「点离端点很远」。
   */
  if (a.length === 1) return polylineHitsPoints(b, a, tolerance)
  if (b.length === 1) return polylineHitsPoints(a, b, tolerance)

  for (let i = 0; i < a.length - 1; i += 1) {
    const a1 = a[i] as Point
    const a2 = a[i + 1] as Point
    for (let j = 0; j < b.length - 1; j += 1) {
      const b1 = b[j] as Point
      const b2 = b[j + 1] as Point
      if (segmentDistanceSq(a1, a2, b1, b2) <= tolSq) return true
    }
  }
  return false
}

export interface CutCandidate<T> {
  /** 命中后要返回的标识（通常是边的 id） */
  key: T
  /** 这条元素的采样点（画布坐标） */
  points: readonly Point[]
}

/** 找出被这条折线划过的所有元素 */
export function findCutTargets<T>(
  polyline: readonly Point[],
  candidates: readonly CutCandidate<T>[],
  tolerance: number,
): T[] {
  const hits: T[] = []
  for (const candidate of candidates) {
    if (polylineHitsPolyline(polyline, candidate.points, tolerance)) {
      hits.push(candidate.key)
    }
  }
  return hits
}

/**
 * 给记录下来的轨迹抽稀：相邻点距离小于 `minDistance` 的丢掉。
 *
 * 指针事件密度很高，不抽稀的话一条短划线可能有几百个点，
 * 每帧都要拿几百条线段去比几十条连线，纯属浪费。
 */
export function simplifyPolyline(
  points: readonly Point[],
  minDistance: number,
): Point[] {
  if (points.length <= 2) return [...points]
  const out: Point[] = [points[0] as Point]
  for (let i = 1; i < points.length; i += 1) {
    const p = points[i] as Point
    const last = out[out.length - 1] as Point
    if (Math.hypot(p.x - last.x, p.y - last.y) >= minDistance) out.push(p)
  }
  // 末尾点一定要留，否则最后一段划痕会丢
  const tail = points[points.length - 1] as Point
  const lastOut = out[out.length - 1] as Point
  if (lastOut.x !== tail.x || lastOut.y !== tail.y) out.push(tail)
  return out
}

/** 折线转成 SVG 的 `points` 属性（画那条划痕用） */
export function polylineToSvgPoints(polyline: readonly Point[]): string {
  return polyline.map((p) => `${p.x},${p.y}`).join(' ')
}

/**
 * **这一次按下要不要进划痕模式，以及是哪一种**。
 *
 * ```text
 *   Alt + 左键拖           → 'cut'    划过剪断连线
 *   Alt + Shift + 左键拖   → 'mute'   划过静音连线
 *   其它一律               → null     交给画布（框选 / 拖节点 / 起线）
 * ```
 *
 * ⚠️ 这段判断原来是写在 `useCutLasso.onLassoDown` 里的一行三元表达式，
 * 于是"哪个修饰键对应哪种划痕"这条规则**只能靠手动试**。抽成纯函数之后
 * `verify:gestures` 能直接断言它（而 `mount-app` 那边再验"Alt 按下去了、
 * 画布上真的出现了 `is-cut` 的划痕层"）。
 *
 * ⚠️ 只认**左键**（`button === 0`）：中键是平移、右键是菜单，
 * 让它们进划痕模式会把三种手势搅在一起。`button` 缺省时按左键处理 ——
 * 合成事件常常不带它，而"带 Alt 就是划痕"这个语义仍然成立。
 */
export function lassoModeOf(event: {
  altKey: boolean
  shiftKey: boolean
  button?: number
}): 'cut' | 'mute' | null {
  if (!event.altKey) return null
  if (event.button !== undefined && event.button !== 0) return null
  return event.shiftKey ? 'mute' : 'cut'
}
